import { useState } from 'react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Avatar } from '../../../avatar';
import { Button } from '../../../button';
import { ImagePreview, useImagePreviewModalControl } from '../../../image-preview';
import { Modal, useModalControl } from '../../../modal';
import { Text } from '../../../text';
import { useChatBubbleContext } from '../../chat';
import { ChatStyles } from '../../chat.styles';

type ImageBubbleProps = {
  src: string;
  suppressPreview?: boolean;
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onClickDownload?: () => void;
  downloadImageTrackingId?: string;
};
export const ImageBubble = ({
  src,
  suppressPreview,
  onError,
  onClickDownload,
  downloadImageTrackingId,
}: ImageBubbleProps) => {
  const [error, setError] = useState(false);
  const previewModalProps = useModalControl();
  const { triggerProps, modalProps: imagePreviewModalProps } = useImagePreviewModalControl();
  const { senderName, timestamp } = useChatBubbleContext();

  return (
    <>
      <figure {...(suppressPreview && src ? {} : triggerProps)}>
        {src && !error && (
          <img
            className='img-bubble'
            src={src}
            onError={(e) => {
              setError(true);
              onError?.(e);
            }}
            css={{
              cursor: !suppressPreview ? 'pointer' : 'default',
            }}
          />
        )}
        {error && (
          <>
            <Icon name='warning-badge' color='error' />
            <Text>There was an error loading this image.</Text>
          </>
        )}
      </figure>
      {src && (
        <PreviewModal
          src={src}
          previewModalProps={previewModalProps}
          onClickDownload={onClickDownload}
          downloadImageTrackingId={downloadImageTrackingId}
          imagePreviewModalProps={imagePreviewModalProps}
          senderName={senderName}
          timestamp={timestamp}
        />
      )}
    </>
  );
};

type PreviewModalProps = {
  src: string;
  previewModalProps: ReturnType<typeof useModalControl>;
  onClickDownload?: () => void;
  downloadImageTrackingId?: string;
  imagePreviewModalProps: ReturnType<typeof useImagePreviewModalControl>['modalProps'];
  senderName?: string;
  timestamp?: string | Date;
};
export const PreviewModal = ({
  src,
  previewModalProps,
  onClickDownload,
  downloadImageTrackingId,
  imagePreviewModalProps,
  senderName,
  timestamp,
}: PreviewModalProps) => {
  const modalStyles = ChatStyles.previewModal;

  //Trying the new ImagePreview Modal, will add a wetag to switch
  return (
    <ImagePreview src={src} alt='Image Preview' onClose={imagePreviewModalProps.onClose}>
      <ImagePreview.Modal
        {...imagePreviewModalProps}
        heading={
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <Avatar name={senderName} size='small' />
            <div css={{ display: 'flex', flexDirection: 'column', marginLeft: theme.spacing(1) }}>
              <Text color='white' size='small' css={{ display: 'flex' }}>
                {senderName}
              </Text>
              <Text color='white' size='small'>
                {timestamp?.toString()}
              </Text>
            </div>
          </div>
        }
      >
        <ImagePreview.Body>
          <ImagePreview.ImageContent />
        </ImagePreview.Body>
        <ImagePreview.Controls />
      </ImagePreview.Modal>
    </ImagePreview>
  );
  return (
    <Modal css={modalStyles} {...previewModalProps.modalProps}>
      <div className='btn-containers'>
        {onClickDownload && (
          <Button
            onClick={onClickDownload}
            iconName='download'
            variant='secondary'
            className='download-button'
            {...(downloadImageTrackingId && { trackingId: downloadImageTrackingId })}
          />
        )}
        <Button
          size='small'
          onClick={previewModalProps.closeModal}
          className='close-button'
          variant='secondary'
          iconName='x'
        />
      </div>
      <Modal.Body>
        <img
          src={src}
          css={{
            maxWidth: '70vw',
            maxHeight: '90vh',
            padding: 0,
          }}
        />
      </Modal.Body>
    </Modal>
  );
};
